<script lang="ts">
  import Drawer from '@athena-lib/Header/Drawer/Drawer.svelte';
  import AddedToCartView from '@athena-lib/AddedToCartView/AddedToCartView.svelte';

  import MiniCart from '@athena-lib/MiniCart/MiniCart.svelte';
  import { EstimatedDelivery } from '@athena-lib/EstimatedDelivery';
  import { DeliveryDetails, getSimpleShippingMethods } from '@athena-lib/DeliveryDetails';

  import { Close, Spinner } from '@athena-lib/Icons';
  import { focusTrap } from '@athena-lib/focusTrap.svelte';
  import {
    createCurrencyFormatter,
    getOrderTotalProps,
    getEstimatedDeliveryProps,
    type CheckoutSdk,
  } from '@athena-lib/Checkout/utils';
  import { lineItemToQuoteCard } from '@athena-lib/QuoteCard/QuoteCard.svelte';
  import { CheckoutLoginForm } from '@athena-lib/CheckoutLoginForm';
  import type { LoginFormHandler } from '@athena-lib/LoginForm';
  import clsx from 'clsx';
  import { getLocalizationContext } from '@athena-lib/i18n';
  import { getLeftToFreeShipping } from '@athena-lib/LeftToFreeShipping';
  import { getCustomStickersBundleProps } from '@athena-lib/CustomStickersBundle';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import type { LoginFormTrackingEvent } from '@athena-lib/LoginForm/types';
  import { Paragraph } from '@athena-lib/Text';
  import { Modal } from '@athena-lib/Modal';
  import { Button } from '@athena-lib/Button';
  import { Dropdown } from '@athena-lib/Fields';
  import { onMount } from 'svelte';
  import type { ShippingCountry } from '@stickerapp-org/checkout-sdk';
  import LocationPicker from './LocationPicker.svelte';
  import { openEditor } from './GlobalEditor.svelte';
  import PreviewModal, { type PreviewSticker } from './PreviewModal.svelte';
  import {
    checkoutDrawerContext,
    checkoutDrawerActions,
  } from '$lib/utilities/checkoutDrawerContext.svelte';
  import {
    getCustomSticker,
    getProductsTranslators,
    getSuggestedPostalCode,
  } from '$lib/checkout.svelte';
  import {
    getCartEventsTracker,
    getEnhancedCommereceEventsTracker,
  } from '$lib/gtmEventHelper.svelte';

  import { loadCheckoutWithDefaults } from '$lib/utilities/checkout';

  type CheckoutDrawerProps = {
    checkoutSdk: CheckoutSdk;
    onSubmitLogin: LoginFormHandler;
    onSubmitResetPassword: LoginFormHandler;
    onSubmitSignUp: LoginFormHandler;
    onTracking?: (message: LoginFormTrackingEvent) => void;
  };

  type PreviewModalState = { open: false; item: undefined } | { open: true; item: PreviewSticker };

  const {
    checkoutSdk,
    onSubmitLogin,
    onSubmitResetPassword,
    onSubmitSignUp,
    onTracking,
  }: CheckoutDrawerProps = $props();

  let previewModalState = $state<PreviewModalState>({
    open: false,
    item: undefined,
  });

  $effect(() => {
    document.body.classList.add('checkout');
    return () => {
      document.body.classList.remove('checkout');
    };
  });
  const formatCurrency = $derived(createCurrencyFormatter(checkoutSdk.state));
  const close = checkoutDrawerActions.close;
  const isOpen = $derived(checkoutDrawerContext.value !== null);
  const locale = getLocalizationContext();

  let loadCheckoutPromise = $state<null | Promise<void>>(null);
  let availableCountries = $state<undefined | Record<string, string>[]>(undefined);

  const cartEventsTracker = getCartEventsTracker('cart_drawer');
  const commerceEventsTracker = getEnhancedCommereceEventsTracker();
  const translations = getCommonTranslations();
  let shipToAnotherModal = $state<{ type: 'open'; value: string } | { type: 'closed' }>({
    type: 'closed',
  });
  let countries = $state<ShippingCountry[] | undefined>(undefined);

  let oldPage = $state(checkoutDrawerContext?.value?.type);
  $effect(() => {
    const newPage = checkoutDrawerContext?.value?.type;
    if (newPage === 'MiniCart' && oldPage !== newPage) {
      commerceEventsTracker.viewCart({ eventAction: 'view_cart_modal' });
      cartEventsTracker.viewMiniCart();
    }
    oldPage = newPage;
  });

  $effect(() => {
    if (
      checkoutDrawerContext?.value?.type !== 'MiniCart' &&
      checkoutDrawerContext?.value?.type !== 'DeliveryDetails'
    ) {
      return;
    }

    if (checkoutSdk.state.checkout === undefined) {
      loadCheckoutPromise = loadCheckoutWithDefaults();
    } else if (!loadCheckoutPromise) {
      loadCheckoutPromise = Promise.resolve(undefined);
    }

    getAvailableCountries().then((countries) => {
      availableCountries = countries;
    });
  });

  const getAvailableCountries = async (): Promise<Record<string, string>[]> => {
    return await (
      await checkoutSdk.countries()
    ).shippingCountries.map((country) => ({
      code: country.code,
      name: country.name,
    }));
  };

  const openPreview = (id: string) => {
    const item = checkoutSdk.state.lines.find((item) => item.id === id) as
      | PreviewSticker
      | undefined;

    if (item) {
      previewModalState = {
        open: true,
        item,
      };
    }
  };

  const closePreview = () => {
    previewModalState = {
      open: false,
      item: undefined,
    };
  };

  onMount(async () => {
    if (checkoutSdk) {
      countries = (await checkoutSdk.countries()).shippingCountries.map((c) => ({
        name: c.name,
        code: c.code,
        active: c.active,
        currency: c.currency,
      }));
    }
  });
</script>

<Drawer label="checkout-drawer" variant="minicart" onClose={close} {isOpen} disablePadding>
  <div
    tabindex="-1"
    class="h-full"
    use:focusTrap
    role="dialog"
    onmouseover={() => {
      if (checkoutDrawerContext?.value?.type === 'ItemAdded') {
        checkoutDrawerContext.value.progressBarState = 'paused';
      }
    }}
    onmouseleave={() => {
      if (checkoutDrawerContext?.value?.type === 'ItemAdded') {
        checkoutDrawerContext.value.progressBarState = 'started';
      }
    }}
    onfocus={() => {
      if (checkoutDrawerContext?.value?.type === 'ItemAdded') {
        checkoutDrawerContext.value.progressBarState = 'paused';
      }
    }}
  >
    <div class="absolute flex w-full flex-col items-end">
      {#if checkoutDrawerContext.value?.type === 'ItemAdded'}
        <div class="h-2.5 w-full bg-st-gray">
          <div
            class={clsx(
              'h-full w-0 bg-st-blue-500 ',
              checkoutDrawerContext.value.progressBarState === 'paused'
                ? 'expand-animation-paused'
                : 'expand-animation',
            )}
            onanimationend={checkoutDrawerActions.close}
          ></div>
        </div>
      {/if}
      <button
        type="button"
        class="ml-auto rounded px-6 py-4 text-4xl font-light"
        onclick={close}
        aria-label="close"
      >
        <Close sizeClass="h-6 w-6"></Close>
      </button>
    </div>

    {#if checkoutDrawerContext?.value?.type === 'Login'}
      <CheckoutLoginForm
        onLogin={onSubmitLogin}
        onResetPassword={onSubmitResetPassword}
        onCreateAccount={onSubmitSignUp}
        googleLoginHref="/customer/google_login"
        {onTracking}
      />
    {/if}

    {#if checkoutDrawerContext?.value?.type === 'MiniCart' && loadCheckoutPromise}
      {#await loadCheckoutPromise}
        <div class="flex h-full w-full items-center justify-center">
          <Spinner sizeClass="h-12 w-12" />
        </div>
      {:then _}
        {@const estimatedDeliveryProps = getEstimatedDeliveryProps(
          checkoutSdk.state,
          getSuggestedPostalCode,
          availableCountries,
        )}
        {@const leftToFreeShipping = getLeftToFreeShipping(checkoutSdk.state, formatCurrency)}
        {@const bundleSavings = getCustomStickersBundleProps(checkoutSdk.state, formatCurrency)}
        <MiniCart
          {leftToFreeShipping}
          orderTotal={getOrderTotalProps(
            checkoutSdk.state,
            checkoutSdk.actions.removeCode,
            bundleSavings,
          )}
          quoteCards={(checkoutSdk.state.lines ?? []).map((item) =>
            lineItemToQuoteCard(
              item,
              formatCurrency,
              checkoutSdk.actions,
              cartEventsTracker,
              () => openEditor(item, 'mini_cart_editor'),
              getCustomSticker,
              getProductsTranslators(),
              getLocalizationContext(),
              getCommonTranslations(),
            ),
          )}
          checkoutUrl={checkoutSdk.state.checkoutPageUrl}
          reviewCartUrl={`${checkoutSdk.state.checkoutPageUrl}?preview`}
          onClick={checkoutDrawerActions.close}
          expressCheckoutElementId={checkoutSdk.state.checkoutElements.express?.payNowElementId}
          customStickersBundle={bundleSavings}
          onItemClick={openPreview}
        >
          {#snippet expectedDelivery()}
            <EstimatedDelivery
              {...estimatedDeliveryProps}
              size="xsmall"
              class="my-2"
              onOpenLocationPicker={checkoutDrawerActions.openLocationPicker}
              {checkoutSdk}
              onOpenCountryPicker={() => {
                shipToAnotherModal = {
                  type: 'open',
                  value: estimatedDeliveryProps?.estimation?.countryCode
                    ? estimatedDeliveryProps?.estimation?.countryCode.toLowerCase()
                    : '',
                };
              }}
            />
          {/snippet}
        </MiniCart>
      {/await}
    {/if}
    {#if checkoutDrawerContext?.value?.type === 'DeliveryDetails' && loadCheckoutPromise}
      {#await loadCheckoutPromise}
        <div class="flex h-full w-full items-center justify-center">
          <Spinner sizeClass="h-12 w-12" />
        </div>
      {:then _}
        {@const estimatedDeliveryProps = getEstimatedDeliveryProps(
          checkoutSdk.state,
          getSuggestedPostalCode,
          availableCountries,
        )}
        {@const simpleShippingMethods = getSimpleShippingMethods(checkoutSdk.state)}
        <DeliveryDetails shippingMethods={simpleShippingMethods}>
          {#snippet estimatedDelivery()}
            <div class="whitespace-pre-line px-2 text-center text-sm">
              <EstimatedDelivery
                {...estimatedDeliveryProps}
                size="medium"
                boldPostalCode
                onOpenLocationPicker={checkoutDrawerActions.openLocationPicker}
                {checkoutSdk}
                onOpenCountryPicker={() => {
                  shipToAnotherModal = {
                    type: 'open',
                    value: estimatedDeliveryProps?.estimation?.countryCode
                      ? estimatedDeliveryProps?.estimation?.countryCode.toLowerCase()
                      : '',
                  };
                }}
              />
            </div>
          {/snippet}
        </DeliveryDetails>
      {/await}
    {/if}
    {#if checkoutDrawerContext?.value?.type === 'LocationPicker'}
      <LocationPicker {checkoutSdk} />
    {/if}
    {#if checkoutDrawerContext?.value?.type === 'ItemAdded'}
      <AddedToCartView
        {getCustomSticker}
        addedItem={checkoutDrawerContext.value.addedItem}
        upsellItems={[]}
        onClickCartLink={checkoutDrawerActions.openMiniCart}
      />
    {/if}
  </div>
</Drawer>
{#if previewModalState.open}
  <PreviewModal
    item={previewModalState.item}
    onClose={closePreview}
    {getCustomSticker}
    {formatCurrency}
    {locale}
  />
{/if}

{#if shipToAnotherModal.type === 'open'}
  <Modal
    showModal={shipToAnotherModal.type === 'open'}
    onClose={() => {
      shipToAnotherModal = { type: 'closed' };
    }}
    size="sm"
    closable={true}
    class="!z-40"
  >
    <div class="flex flex-col gap-4 p-4">
      <Paragraph size="large" class="text-center" bold
        >{translations.shipToAnotherCountryTitle}</Paragraph
      >
      <Paragraph class="pb-6 text-center">{translations.shipToAnotherCountryDisclaimer}</Paragraph>
      <Dropdown
        name="country"
        class="text-st-black"
        bind:value={shipToAnotherModal.value}
        options={countries?.map((x) => ({
          label: x.name,
          value: x.code.toLowerCase(),
          disabled: !x.active,
        })) ?? []}
        country={shipToAnotherModal.value?.toLowerCase()}
      />
      <Button
        type="button"
        onClick={() => {
          const needle = shipToAnotherModal.type === 'open' ? shipToAnotherModal.value : undefined;
          const foundCountry = countries?.find((x) => x.code.toLowerCase() === needle);
          if (foundCountry) {
            checkoutSdk.updateCountry(foundCountry.code, foundCountry.currency);
            shipToAnotherModal = { type: 'closed' };
          }
        }}>{translations.continue}</Button
      >
    </div>
  </Modal>
{/if}

<style>
  .expand-animation {
    animation: expand 10s linear running;
  }
  .expand-animation-paused {
    animation: expand 10s linear paused;
  }
  @keyframes expand {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }
</style>
